import { gql } from '@graphql/__generated__';
import type {
  GetAgencyDrawerReviewsQuery,
  GetAgencyDrawerTransactionsQuery,
} from '@graphql/__generated__/graphql';
import type {
  DataTransformer,
  NormalizedReview,
  NormalizedTransaction,
  QueryConfig,
} from '@modules/_shared/components/visibility-drawer/queries';
import { getTwoYearsAgo } from '@modules/_shared/utils/dates';
import { getTeamReviewsWhere } from '@modules/_shared/utils/teamReviews';

export const GET_AGENCY_EDIT_DETAILS = gql(/* GraphQL */ `
  query GetAgencyEditDetails($teamId: uuid!) {
    teams_by_pk(id: $teamId) {
      __typename
      id
      name
      agency_slug
      description
      locality
      route
      street_number
      postcode
      state
      country
      lat
      lng
      active_since
      website_url
      facebook_url
      instagram_url
      tiktok_url
      linkedin_url
      x_url
      youtube_url
      show_in_agency_pages
      google_place_description
      idx_ftp_client_id
      idx_agency_id
      listings_agency_names
      phone_numbers(
        order_by: [{ primary: desc }, { created_at: desc }]
        limit: 1
      ) {
        id
        number
        primary
      }
      logo {
        id
        url
      }
      cover_photo {
        id
        url
      }
      teams_users_aggregate {
        aggregate {
          count
        }
      }
      property_transactions_aggregate {
        aggregate {
          count
        }
      }
      teams_users {
        deactivated
        user {
          id
          show_in_agency_pages
          property_transactions_aggregate {
            aggregate {
              count
            }
          }
        }
      }
    }
  }
`);

export const GET_AGENCY_DRAWER_REVIEWS = gql(/* GraphQL */ `
  query GetAgencyDrawerReviews($reviewsWhere: reviews_bool_exp!) {
    reviews(where: $reviewsWhere, order_by: { completed_at: desc }) {
      id
      rating
      review_text
      completed_at
      display_name
      completed
      transaction_id
      owner_answer
      owner_answer_date
    }
  }
`);

export const GET_AGENCY_REVIEWS_COUNT = gql(/* GraphQL */ `
  query GetAgencyReviewsCount($reviewsWhere: reviews_bool_exp!) {
    reviews_aggregate(where: $reviewsWhere) {
      aggregate {
        count
        avg {
          rating
        }
      }
    }
  }
`);

export const GET_TEAM_DRAWER_TRANSACTIONS = gql(/* GraphQL */ `
  query GetAgencyDrawerTransactions(
    $teamId: uuid!
    $limit: Int!
    $offset: Int!
    $twoYearsAgo: timestamptz!
  ) {
    property_transactions(
      where: {
        seller_broker_team_id: { _eq: $teamId }
        signature_date: { _gte: $twoYearsAgo, _lte: "now()" }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        is_published: { _eq: true }
      }
      order_by: { signature_date: desc }
      limit: $limit
      offset: $offset
    ) {
      id
      property_id
      signature_date
      purchase_price
      property {
        id
        property_type {
          label
        }
        number_of_rooms
        locality_place {
          label
        }
        property_images(limit: 1, order_by: { order_nr: asc }) {
          image {
            url
          }
        }
      }
      reviews_aggregate(where: { completed: { _eq: true } }) {
        aggregate {
          count
        }
      }
      seller_broker {
        id
        first_name
        last_name
      }
      seller_broker_team {
        id
        name
      }
    }
  }
`);

export const GET_AGENCY_TRANSACTIONS_COUNT = gql(/* GraphQL */ `
  query GetAgencyTransactionsCount($teamId: uuid!, $twoYearsAgo: timestamptz!) {
    property_transactions_aggregate(
      where: {
        seller_broker_team_id: { _eq: $teamId }
        signature_date: { _gte: $twoYearsAgo, _lte: "now()" }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        is_published: { _eq: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const agencyReviewsQuery: QueryConfig = {
  query: GET_AGENCY_DRAWER_REVIEWS,
  getVariables: ({ entityId }) => ({
    reviewsWhere: getTeamReviewsWhere(entityId),
  }),
};

export const teamReviewsTransformer: DataTransformer<
  GetAgencyDrawerReviewsQuery,
  NormalizedReview
> = (data, options) => {
  // Already sorted newest first by the query.
  const sortedReviews = data.reviews;

  const limit = options?.limit ?? 5;
  const offset = options?.offset ?? 0;
  const paginatedReviews = sortedReviews.slice(offset, offset + limit);

  return {
    items: paginatedReviews.map(review => ({
      id: review.id,
      rating: review.rating,
      reviewText: review.review_text,
      completedAt: review.completed_at,
      displayName: review.display_name,
      ownerAnswer: review.owner_answer,
      ownerAnswerDate: review.owner_answer_date,
      isVerified: !!review.transaction_id,
    })),
    totalCount: 0, // Count will be fetched separately
    averageRating: 0, // Average will be fetched separately
  };
};

export const teamTransactionsQuery: QueryConfig = {
  query: GET_TEAM_DRAWER_TRANSACTIONS,
  getVariables: ({ entityId, limit, offset }) => {
    return {
      teamId: entityId,
      limit: limit || 5,
      offset: offset || 0,
      twoYearsAgo: getTwoYearsAgo().toISOString().slice(0, 10),
    };
  },
};

export const teamTransactionsTransformer: DataTransformer<
  GetAgencyDrawerTransactionsQuery,
  NormalizedTransaction
> = data => {
  const transactions = data.property_transactions ?? [];

  return {
    items: transactions.map(transaction => {
      const property = transaction.property;
      const hasReviews =
        (transaction.reviews_aggregate?.aggregate?.count ?? 0) > 0;

      return {
        id: transaction.id,
        propertyId: transaction.property_id,
        signatureDate: transaction.signature_date,
        purchasePrice: transaction.purchase_price,
        propertyType: property?.property_type?.label ?? null,
        numberOfRooms: property?.number_of_rooms ?? null,
        locality: property?.locality_place?.label ?? null,
        imageUrl: property?.property_images?.[0]?.image?.url ?? null,
        isVerified: hasReviews,
        soldByAgent: transaction.seller_broker
          ? `${transaction.seller_broker.first_name} ${transaction.seller_broker.last_name}`
          : null,
        soldByTeam: transaction.seller_broker_team?.name ?? null,
      };
    }),
    totalCount: 0, // Count will be fetched separately
  };
};
